import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Typography,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  CalendarMonth,
  People,
  EventBusy,
  SwapHoriz,
  Assessment,
  ImportExport,
  Settings,
  School,
  SupervisorAccount,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { BrandingFooter } from './BrandingFooter';
import { useQuery } from '@tanstack/react-query';
import { getAbsences } from '@timetable/api';
import { queryKeys } from '@/lib/queryKeys';

interface SidebarProps {
  drawerWidth: number;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  feature?: string;
  badgeCount?: number;
}

export function Sidebar({ drawerWidth }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const role = useAuthStore((state) => state.role);
  const user = useAuthStore((state) => state.user);
  const {
    canEditTimetable,
    canViewMasterData,
    canViewAbsences,
    canViewReports,
    canUseImportExport,
    canViewSettings,
  } = useFeatureAccess();
  
  const isSuperAdmin = role === 'super_admin';

  // Badge count: absences that need a substitution plan
  const { schoolContext } = useAuthStore();
  const { data: pendingAbsences } = useQuery({
    queryKey: queryKeys.absences.list({ school_id: schoolContext || '', _sidebar: true }),
    queryFn: () =>
      getAbsences({ school_id: schoolContext || '', status: 'all', page: 1, page_size: 200 }),
    enabled: !!schoolContext && canViewAbsences,
    refetchInterval: 60_000,
  });
  const pendingCount = (pendingAbsences || []).filter(
    (a) => a.status === 'pending' || a.status === 'draft_pending'
  ).length;

  const navItems: NavItem[] = [
    {
      label: 'Dashboard',
      path: '/app/dashboard',
      icon: <Dashboard />,
    },
    {
      label: 'Timetable Designer',
      path: '/app/timetable',
      icon: <CalendarMonth />,
      feature: 'timetable',
    },
    {
      label: 'Master Data',
      path: '/app/master-data',
      icon: <People />,
      feature: 'master-data',
    },
    {
      label: 'Absences',
      path: '/app/absences',
      icon: <EventBusy />,
      feature: 'absences',
    },
    {
      label: 'Substitutions',
      path: '/app/substitutions',
      icon: <SwapHoriz />,
      feature: 'absences',
      badgeCount: pendingCount,
    },
    {
      label: 'Reports',
      path: '/app/reports',
      icon: <Assessment />,
      feature: 'reports',
    },
    {
      label: 'Import/Export',
      path: '/app/import-export',
      icon: <ImportExport />,
      feature: 'import-export',
    },
    {
      label: 'Settings',
      path: '/app/settings',
      icon: <Settings />,
      feature: 'settings',
    },
  ];

  const superAdminNavItems: NavItem[] = [
    {
      label: 'School Management',
      path: '/super-admin/schools',
      icon: <School />,
    },
    {
      label: 'User Management',
      path: '/super-admin/users',
      icon: <SupervisorAccount />,
    },
  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => {
    if (!item.feature) return true;
    
    switch (item.feature) {
      case 'timetable':
        return canEditTimetable;
      case 'master-data':
        return canViewMasterData;
      case 'absences':
        return canViewAbsences;
      case 'reports':
        return canViewReports;
      case 'import-export':
        return canUseImportExport;
      case 'settings':
        return canViewSettings;
      default:
        return true;
    }  });

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth color="primary" />
          <Typography variant="h6" noWrap>
            Timetable
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            Main
          </Typography>
        </Box>
        <List>
          {visibleNavItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2.5,
                  minHeight: 48,
                }}
              >
                <ListItemIcon>
                  {item.badgeCount ? (
                    <Badge badgeContent={item.badgeCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {/* Super Admin Section */}
        {isSuperAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SUPER ADMIN
              </Typography>
            </Box>
            <List>
              {superAdminNavItems.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      mx: 1,
                      mb: 0.5,
                      borderRadius: 2.5,
                      minHeight: 48,
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar sx={{ width: 34, height: 34 }}>
          {user?.email?.[0]?.toUpperCase() ?? 'A'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {user?.email?.split('@')[0] ?? 'Admin User'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {role?.replace('_', ' ') ?? 'School administrator'}
          </Typography>
        </Box>
      </Box>
      <BrandingFooter variant="compact" />
    </Box>
  );

  return (
    <>
      {/* Desktop Persistent Drawer */}
      <Drawer
        variant="persistent"
        open={sidebarOpen}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Mobile Temporary Drawer */}
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={toggleSidebar}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
